import { type InputHTMLAttributes, forwardRef, useId, useState } from "react";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
	label?: string;
	error?: string;
	helper?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
	{ label, error, helper, className = "", id, type, ...props },
	ref,
) {
	const generatedId = useId();
	const inputId = id ?? generatedId;
	const isPassword = type === "password";
	const [revealed, setRevealed] = useState(false);
	const effectiveType = isPassword && revealed ? "text" : type;

	return (
		<div className="flex flex-col gap-1.5">
			{label && (
				<label
					htmlFor={inputId}
					className="text-sm font-medium text-foreground"
				>
					{label}
				</label>
			)}
			<div className="relative">
				<input
					ref={ref}
					id={inputId}
					type={effectiveType}
					aria-invalid={error ? "true" : "false"}
					aria-describedby={
						error
							? `${inputId}-error`
							: helper
								? `${inputId}-helper`
								: undefined
					}
					// text-base (16px) on mobile prevents iOS Safari's auto-zoom on
					// focus — text-sm (14px) triggers it. Bump height to 44px on
					// mobile too (iOS minimum touch-target guideline).
					className={`h-11 sm:h-10 w-full rounded-lg border bg-card text-card-foreground px-3 ${isPassword ? "pe-10" : ""} text-base sm:text-sm transition-colors placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-60 ${error ? "border-danger" : "border-border"} ${className}`}
					{...props}
				/>
				{isPassword && (
					<button
						type="button"
						tabIndex={-1}
						onClick={() => setRevealed((v) => !v)}
						aria-label={revealed ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"}
						aria-pressed={revealed}
						className="absolute end-2 top-1/2 -translate-y-1/2 size-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted flex items-center justify-center"
					>
						{revealed ? <EyeOffIcon /> : <EyeIcon />}
					</button>
				)}
			</div>
			{error && (
				<p id={`${inputId}-error`} className="text-xs text-danger">
					{error}
				</p>
			)}
			{helper && !error && (
				<p id={`${inputId}-helper`} className="text-xs text-muted-foreground">
					{helper}
				</p>
			)}
		</div>
	);
});

function EyeIcon() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			focusable={false}
		>
			<path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
			<circle cx="12" cy="12" r="3" />
		</svg>
	);
}

function EyeOffIcon() {
	return (
		<svg
			width="16"
			height="16"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			focusable={false}
		>
			<path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
			<path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
			<path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
			<line x1="2" x2="22" y1="2" y2="22" />
		</svg>
	);
}
